import { useCallback } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAppDispatch, useAppSelector } from "@/state/redux/hooks";
import {
  requestOpenCollectionsSidebar,
  requestOpenTimeSidebar,
  selectCollectionsSidebarOpen,
  selectTimeSidebarOpen,
  setCollectionsSidebarOpen,
  setTimeSidebarOpen,
} from "@/state/redux/slices/ui";

/**
 * Read and toggle a sidebar from outside its SidebarProvider.
 *
 * The header lives between the two panels and belongs to neither context, so
 * it cannot use `useSidebar()` (which resolves the nearest provider) or shadcn's
 * `SidebarTrigger`. The two breakpoints are different mechanisms and are kept
 * that way: on mobile the panel is a Sheet whose open state stays inside the
 * provider, reached through the existing one-shot request actions; on desktop
 * the state is in Redux and the provider is controlled from it.
 *
 * `open` is the desktop flag, so it is only meaningful above the 768px
 * boundary that `sidebar.tsx` itself switches on.
 */
function useSidebarToggle(
  open: boolean,
  onDesktop: (open: boolean) => { payload: boolean; type: string },
  onMobile: (open: boolean) => { payload: boolean; type: string },
) {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();

  const toggle = useCallback(() => {
    // Mobile only ever opens: the sheet closes itself on its overlay/Esc.
    dispatch(isMobile ? onMobile(true) : onDesktop(!open));
  }, [dispatch, isMobile, onMobile, onDesktop, open]);

  return { open, toggle };
}

export function useTimeSidebarToggle() {
  return useSidebarToggle(
    useAppSelector(selectTimeSidebarOpen),
    setTimeSidebarOpen,
    requestOpenTimeSidebar,
  );
}

export function useCollectionsSidebarToggle() {
  return useSidebarToggle(
    useAppSelector(selectCollectionsSidebarOpen),
    setCollectionsSidebarOpen,
    requestOpenCollectionsSidebar,
  );
}
